import { Noto_Sans_TC } from "next/font/google";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import localeCore from "../../core/locale.core";
import useCanDoShop from "../../hooks/useCanDoShop.hook";
import useNavCollapse from "../../hooks/useNavCollapose.hook";
import useShop from "../../hooks/useShop.hook";
import Bar2Icon from "../../icons/bar2.icon";
import ChevronRightIcon from "../../icons/chevronRight.icon";
import CloseIcon from "../../icons/iconClose.comp";
import Nav from "../menu/nav.comp";
import DesktopNavExpandBtn from "./desktopNavExpandBtn.comp";
import TopRightPanelComp from "./topRightPanel.comp";
import ShopList from "../profile/profileShopList";

const notoSansTC = Noto_Sans_TC({ weight: ["100", "200", "300", "400", "500", "600", "700", "800", "900"], subsets: ["latin"] });

interface BreadcumbsItem {
  name: string;
  link?: string;
}

interface Props {
  seperator?: boolean;
  title: string;
  children: React.ReactNode;
  breadcrumbs?: BreadcumbsItem[];
  titleBtn?: React.ReactNode;
  hideTitle?: boolean;
  cap?: ShopCap;
  rightBtns?: React.ReactNode[];
}

function Title({ title }: { title: string }) {
  const { selectedShop } = useShop();

  return (
    <Head>
      <title>{getTitle(title, selectedShop)}</title>
    </Head>
  );
}

export default function Layout({ title, children, titleBtn, hideTitle, cap, seperator, breadcrumbs, rightBtns }: Props) {
  const { push, isReady } = useRouter();
  const canDo = useCanDoShop(cap);

  useEffect(() => {
    if (canDo === null) return;
    if (!canDo && isReady) {
      push("/");
    }
  }, [isReady, canDo, push]);

  return (
    <>
      <Title title={title} />
      <div
        className={`min-h-screen flex text-black dark:text-her-gray bg-her-lightgray dark:bg-her-backblue ${notoSansTC.className}`}
      >
        <DesktopNav />
        <div className="grow shrink px-2 sm:pr-8 sm:pl-4 relative h-screen overflow-auto">
          <TopPanel>
            <div className="flex justify-between gap-2">
              <div className={`flex flex-row gap-4 items-center ${seperator ? "justify-between" : ""}`}>
                {!hideTitle && <h1 className="text-2xl text-slate-900 dark:text-slate-100 tracking-wide">{title}</h1>}
                {titleBtn}
              </div>
              {rightBtns}
            </div>
          </TopPanel>
          <Breadcrumbs items={breadcrumbs} />

          <main className="mt-4 space-y-4">{children}</main>
        </div>
      </div>
    </>
  );
}

function Breadcrumbs({ items }: { items?: BreadcumbsItem[] }) {
  return (
    <section className="-mb-4 flex items-center gap-2 text-xs">
      {items?.map((item, index) => {
        return (
          <div key={index} className="flex items-center">
            {index + 1 !== items.length ? (
              <div className="flex items-center gap-2">
                <Link href={item.link ?? ""}>{item.name}</Link>
                <ChevronRightIcon className="w-3" />
              </div>
            ) : (
              <span>{item.name}</span>
            )}
          </div>
        );
      })}
    </section>
  );
}
const TopPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      {/* Desktop Layout */}
      <div className="hidden sm:flex sm:flex-row-reverse items-center justify-between py-4">
        <TopRightPanelComp />
        <div className="flex gap-4">
          <DesktopNavExpandBtn />
          {children}
        </div>
      </div>

      {/* Mobile Layout */}
      <div id="mobile-layout" className="sm:hidden space-y-2 mb-4">
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <MobileNav />
            <ShopList />
          </div>
          <TopRightPanelComp />
        </div>
        {children}
      </div>
    </>
  );
};

function DesktopNav() {
  const [collapse] = useNavCollapse();

  let css =
    "hidden " + // Alyway hidden in mobile layout
    "shrink-0 bg-her-bg dark:bg-her-darkbg h-screen overflow-auto " +
    "relative "; // for collapse btn

  if (!collapse) {
    css += "md:basis-[13rem] md:block "; // show it in desktop layout when not collapsed
  }

  return <Nav className={css} />;
}

function getTitle(title: string, selectedShop: any) {
  if (!selectedShop) return `${title} - HER`;

  return `${title} - ${localeCore.getLocaleValueDefaultFallback(selectedShop.name)} - HER`;
}

const MobileNav = () => {
  const [showNav, setShowNav] = useState(false);

  if (showNav) {
    return (
      <>
        <button id="mobile-nav-closebtn" className="md:hidden fixed right-0 top-2 z-50 p-2" onClick={() => setShowNav(false)}>
          <CloseIcon className="w-8 h-8" />
        </button>

        <Nav className="md:hidden overflow-y-scroll fixed z-40 top-0 left-0 w-full h-[100dvh] bg-her-bg dark:bg-her-darkbg pb-4" />
      </>
    );
  }

  return (
    <button id="mobie-nav-showBtn" className="md:hidden" onClick={() => setShowNav(true)}>
      <Bar2Icon className="w-8 h-8" />
    </button>
  );
};
